import React from "react";
import PropTypes from "prop-types";
import { Col, Form, InputNumber, Row, Select } from "antd";

import DashedTooltip from "components/UI/DashedTooltip";

export const WEEKDAY_OPTIONS = [
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
];

export const OPTION_PARAM_DEFAULTS = {
  orderSizeType: "percent",
  orderSize: 5,
  maxOpenOrders: 1,
  minDays: 2,
  maxDteBars: 5,
  excludedEntryWeekdays: [],
};

// Same rules as the backend (option-strategies.controller.js validateParams).
const sizingRule = ({ getFieldValue }) => ({
  validator(_, value) {
    const isPercent = getFieldValue("orderSizeType") === "percent";
    const total = (value || 1) * (getFieldValue("orderSize") || 0);
    return isPercent && total > 100
      ? Promise.reject(new Error("Max positions × order size exceeds 100%"))
      : Promise.resolve();
  },
});

const expiryWindowRule = ({ getFieldValue }) => ({
  validator(_, value) {
    return value !== undefined && value < (getFieldValue("minDays") || 1)
      ? Promise.reject(new Error("Must be at least the min bars"))
      : Promise.resolve();
  },
});

// Trading rules of an option strategy. Field names match `params` on the
// backend so a form's values can be sent as-is.
const OptionStrategyFields = (props) => {
  const { disabled = false } = props;
  const span = { xs: 24, sm: 12, lg: 8 };

  return (
    <Row gutter={16}>
      <Col {...span}>
        <Form.Item
          name="takeProfitPercent"
          label={
            <DashedTooltip text="Exit when the underlying gains this % from its price at the buy signal (15:50 ET)">
              Take profit (underlying %)
            </DashedTooltip>
          }
          rules={[{ required: true, message: "Take profit is required" }]}
        >
          <InputNumber
            min={0.1}
            step={0.5}
            addonAfter="%"
            style={{ width: "100%" }}
            disabled={disabled}
          />
        </Form.Item>
      </Col>
      <Col {...span}>
        <Form.Item
          name="minDays"
          label={
            <DashedTooltip text="Enter only if an expiry exists at least this many trading bars away">
              Min bars to expiry
            </DashedTooltip>
          }
          rules={[{ required: true, message: "Required" }]}
        >
          <InputNumber
            min={1}
            precision={0}
            style={{ width: "100%" }}
            disabled={disabled}
          />
        </Form.Item>
      </Col>
      <Col {...span}>
        <Form.Item
          name="maxDteBars"
          dependencies={["minDays"]}
          label={
            <DashedTooltip text="...and at most this many trading bars away. The nearest expiry in the window is used; with none, the signal is skipped">
              Max bars to expiry
            </DashedTooltip>
          }
          rules={[{ required: true, message: "Required" }, expiryWindowRule]}
        >
          <InputNumber
            min={1}
            precision={0}
            style={{ width: "100%" }}
            disabled={disabled}
          />
        </Form.Item>
      </Col>
      <Col {...span}>
        <Form.Item label="Order size" required>
          <Form.Item
            name="orderSize"
            noStyle
            rules={[{ required: true, message: "Required" }]}
          >
            <InputNumber
              min={0.01}
              style={{ width: "100%" }}
              disabled={disabled}
              addonAfter={
                <Form.Item name="orderSizeType" noStyle>
                  <Select style={{ width: 110 }} disabled={disabled}>
                    <Select.Option value="percent">% of equity</Select.Option>
                    <Select.Option value="amount">$</Select.Option>
                  </Select>
                </Form.Item>
              }
            />
          </Form.Item>
        </Form.Item>
      </Col>
      <Col {...span}>
        <Form.Item
          name="maxOpenOrders"
          label="Max open positions"
          dependencies={["orderSize", "orderSizeType"]}
          rules={[{ required: true, message: "Required" }, sizingRule]}
        >
          <InputNumber
            min={1}
            precision={0}
            style={{ width: "100%" }}
            disabled={disabled}
          />
        </Form.Item>
      </Col>
      <Col {...span}>
        <Form.Item name="excludedEntryWeekdays" label="No entries on">
          <Select
            mode="multiple"
            allowClear
            options={WEEKDAY_OPTIONS}
            placeholder="Any weekday"
            disabled={disabled}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

OptionStrategyFields.propTypes = {
  disabled: PropTypes.bool,
};

export const PARAM_KEYS = Object.keys(OPTION_PARAM_DEFAULTS).concat(
  "takeProfitPercent",
);

export const pickParams = (values) =>
  Object.fromEntries(PARAM_KEYS.map((key) => [key, values[key]]));

export default OptionStrategyFields;
